import { ReactNode, useMemo, useState } from 'react';
import { Button } from '@tigergraph/app-ui-lib/button';
import { Checkbox } from '@tigergraph/app-ui-lib/checkbox';
import { ProgressBar } from '@tigergraph/app-ui-lib/progress-bar';
import { useStyletron } from '@tigergraph/app-ui-lib/Theme';
import { Plus } from 'baseui/icon';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAtom } from 'jotai';

import { useOrgContext } from '@/contexts/orgContext';
import { calculateRoleForSpace } from '@/pages/admin/user/type';
import { ConfirmResumeWorkspace, SettingMenuForWorkSpace } from '@/pages/workgroup/tab/SettingMenu';
import { StatusIndicator } from '@/pages/workgroup/tab/General';
import { WorkspaceNamePopover } from '@/pages/workgroup/tab/general/WorkspaceNamePopover';
import {
  DatabaseT,
  WorkGroupT,
  WorkspaceT,
  isStatusActive,
  isStatusIdle,
  isStatusPaused,
} from '@/pages/workgroup/type';
import { onboardingAtom, defaultOnboardingState } from '@/pages/onboarding/state';
import trackUtil from '@/utils/analytics-service.ts';
import { formatBytes } from '@/utils/format';

import { calculateUtilizationPercent, getWorkspaceFolderSize, parseMemoryToBytes } from './utils';
import { WorkspaceRow } from './types';
import { expand } from 'inline-style-expand-shorthand';

export type CardViewProps = {
  group: WorkGroupT;
  rows: WorkspaceRow[];
  selectedIds: Set<string>;
  onToggleRow: (workspace: WorkspaceT) => void;
  isCreatingDisabled: boolean;
  canCreate: boolean;
  onCreate: () => void;
  emptyMessage?: string;
};

export default function CardView({
  group,
  rows,
  selectedIds,
  onToggleRow,
  isCreatingDisabled,
  canCreate,
  onCreate,
  emptyMessage,
}: CardViewProps) {
  const [css, theme] = useStyletron();

  const databaseById = useMemo(
    () =>
      group.tg_databases.reduce<Record<string, DatabaseT>>((acc, database) => {
        acc[database.database_id] = database;
        return acc;
      }, {}),
    [group.tg_databases]
  );

  const rwRows = useMemo(() => rows.filter((row) => row.workspace.is_rw), [rows]);

  if (rwRows.length === 0) {
    return (
      <div
        className={css({
          padding: '32px 16px',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '12px',
          color: theme.colors['text.secondary'],
          backgroundColor: theme.colors['background.alternative'],
          borderRadius: '12px',
          border: `1px solid ${theme.colors.divider}`,
        })}
      >
        <span>{emptyMessage || 'No workspaces match your filters.'}</span>
        {canCreate ? (
          <Button
            kind="secondary"
            size="compact"
            disabled={isCreatingDisabled}
            onClick={onCreate}
            startEnhancer={<Plus size={16} />}
          >
            Create Workspace
          </Button>
        ) : null}
      </div>
    );
  }

  return (
    <div
      className={css({
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(400px, 1fr))',
        alignItems: 'start',
        gap: '16px',
      })}
    >
      {rwRows.map((row) => {
        const database = row.workspace.database_id ? databaseById[row.workspace.database_id] : undefined;
        return (
          <WorkspaceCard
            key={row.workspace.workspace_id}
            group={group}
            workspace={row.workspace}
            database={database}
            databaseName={database?.name || row.databaseName}
            selectedIds={selectedIds}
            onToggleRow={onToggleRow}
          />
        );
      })}
    </div>
  );
}

function WorkspaceCard({
  group,
  workspace,
  database,
  databaseName,
  selectedIds,
  onToggleRow,
}: {
  group: WorkGroupT;
  workspace: WorkspaceT;
  database: DatabaseT | undefined;
  databaseName: string;
  selectedIds: Set<string>;
  onToggleRow: (workspace: WorkspaceT) => void;
}) {
  const [css, theme] = useStyletron();

  const databaseById = useMemo(
    () =>
      group.tg_databases.reduce<Record<string, DatabaseT>>((acc, db) => {
        acc[db.database_id] = db;
        return acc;
      }, {}),
    [group.tg_databases]
  );

  const snapshotWorkspaces = useMemo(
    () =>
      group.workspaces.filter(
        (w) =>
          w.workspace_id !== workspace.workspace_id &&
          w.database_id === workspace.database_id &&
          workspace.database_id &&
          !w.is_rw
      ),
    [group.workspaces, workspace.workspace_id, workspace.database_id]
  );

  return (
    <div
      className={css({
        display: 'flex',
        flexDirection: 'column',
        gap: '12px',
        padding: '16px',
        borderRadius: '8px',
        border: `1px solid ${theme.colors.divider}`,
        backgroundColor: theme.colors['background.alternative'],
      })}
    >
      <WorkspaceCardBody
        group={group}
        workspace={workspace}
        database={database}
        databaseName={databaseName}
        checked={selectedIds.has(workspace.workspace_id)}
        onToggle={() => onToggleRow(workspace)}
      />

      <SnapshotSection
        group={group}
        databaseName={databaseName}
        snapshots={snapshotWorkspaces}
        databaseById={databaseById}
        selectedIds={selectedIds}
        onToggleRow={onToggleRow}
      />
    </div>
  );
}

function WorkspaceCardBody({
  group,
  workspace,
  database,
  databaseName,
  showDatabaseRow = true,
  checked,
  onToggle,
}: {
  group: WorkGroupT;
  workspace: WorkspaceT;
  database: DatabaseT | undefined;
  databaseName?: string;
  showDatabaseRow?: boolean;
  checked?: boolean;
  onToggle?: () => void;
}) {
  const [css, theme] = useStyletron();
  const { userInfo } = useOrgContext();
  const [showResume, setShowResume] = useState(false);

  const userRoleForSpace = calculateRoleForSpace(userInfo.roles, workspace.workgroup_id, workspace.workspace_id);
  const hasResumePermission = userRoleForSpace === 'workspace-admins';

  const memory = workspace.workspace_type?.memory;
  const typeName = workspace.workspace_type?.typeName;
  const topologyLabel = typeName ? (memory ? `${typeName} (${memory})` : typeName) : '-';

  return (
    <div
      className={css({
        display: 'flex',
        flexDirection: 'column',
        gap: '12px',
      })}
    >
      <CardHeader group={group} workspace={workspace} database={database} checked={checked} onToggle={onToggle} />
      <StatusBadge workspace={workspace} />

      <div
        className={css({
          height: '1px',
          backgroundColor: theme.colors.divider,
        })}
      />

      {showDatabaseRow ? <DetailRow label="Database:" value={<DatabaseValue name={databaseName ?? ''} />} /> : null}
      <DetailRow label="Access:" value={<AccessBadge isRw={workspace.is_rw} />} />
      <DetailRow label="Topology:" value={<span>{topologyLabel}</span>} />

      <DataUtilization workspace={workspace} database={database} />

      <CardFooter
        workspace={workspace}
        hasResumePermission={hasResumePermission}
        onResume={() => setShowResume(true)}
      />

      {showResume ? (
        <ConfirmResumeWorkspace isOpen={showResume} onClose={() => setShowResume(false)} workspace={workspace} />
      ) : null}
    </div>
  );
}

function CardHeader({
  group,
  workspace,
  database,
  checked,
  onToggle,
}: {
  group: WorkGroupT;
  workspace: WorkspaceT;
  database: DatabaseT | undefined;
  checked?: boolean;
  onToggle?: () => void;
}) {
  const [css, theme] = useStyletron();

  return (
    <div
      className={css({
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
      })}
    >
      <WorkspaceNamePopover
        workspace={workspace}
        database={database}
        group={group}
        className={css({
          fontSize: '16px',
          fontWeight: 600,
          color: theme.colors['text.primary'],
          flex: 1,
          minWidth: 0,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        })}
      >
        {workspace.name}
      </WorkspaceNamePopover>
      {onToggle ? (
        <Checkbox
          aria-label={`Select workspace ${workspace.name}`}
          checked={checked}
          onChange={onToggle}
          overrides={{
            Root: {
              style: {
                marginRight: 0,
              },
            },
          }}
        />
      ) : null}
      <SettingMenuForWorkSpace workspace={workspace} database={database} borderless showNavItems />
    </div>
  );
}

function StatusBadge({ workspace }: { workspace: WorkspaceT }) {
  const [css, theme] = useStyletron();
  return (
    <div
      className={css({
        display: 'inline-flex',
        alignSelf: 'flex-start',
        alignItems: 'center',
        gap: '6px',
        padding: '4px 10px',
        backgroundColor: theme.colors['tag.background.default'],
        borderRadius: '14px',
        fontSize: '12px',
      })}
    >
      <StatusIndicator status={workspace.status} errMessage={workspace.err_message} size="compact" />
      <span>{workspace.status}</span>
    </div>
  );
}

function DatabaseValue({ name }: { name: string }) {
  const [css, theme] = useStyletron();
  if (!name) {
    return <span className={css({ color: theme.colors['text.secondary'] })}>-</span>;
  }
  return <span>{name}</span>;
}

function DetailRow({ label, value }: { label: string; value: ReactNode }) {
  const [css, theme] = useStyletron();
  return (
    <div
      className={css({
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: '12px',
        fontSize: '13px',
      })}
    >
      <span
        className={css({
          color: theme.colors['text.secondary'],
        })}
      >
        {label}
      </span>
      <span
        className={css({
          color: theme.colors['text.primary'],
          textAlign: 'right',
        })}
      >
        {value}
      </span>
    </div>
  );
}

function DataUtilization({ workspace, database }: { workspace: WorkspaceT; database: DatabaseT | undefined }) {
  const [css, theme] = useStyletron();
  const totalBytes = parseMemoryToBytes(workspace.workspace_type?.memory);
  const usedBytes = getWorkspaceFolderSize(database, workspace.workspace_id);
  const percent =
    totalBytes !== undefined && usedBytes !== undefined
      ? calculateUtilizationPercent(usedBytes, totalBytes)
      : undefined;

  if (totalBytes === undefined || usedBytes === undefined) {
    return null;
  }

  return (
    <div
      className={css({
        display: 'flex',
        flexDirection: 'column',
        gap: '4px',
      })}
    >
      <span
        className={css({
          fontSize: '13px',
          color: theme.colors['text.secondary'],
        })}
      >
        Data Utilization
      </span>
      <div
        className={css({
          display: 'flex',
          justifyContent: 'space-between',
          fontSize: '12px',
          color: theme.colors['text.secondary'],
        })}
      >
        <span>
          {formatBytes(usedBytes)} / {workspace.workspace_type.memory}
        </span>
        {percent !== undefined ? <span>{percent}%</span> : null}
      </div>
      <ProgressBar
        value={percent ?? 0}
        overrides={{
          BarContainer: {
            style: {
              marginLeft: 0,
              marginRight: 0,
            },
          },
          Bar: {
            style: {
              height: '8px',
              backgroundColor: '#E5E7EB',
              ...expand({
                borderRadius: '10px',
              }),
            },
          },
          BarProgress: {
            style: {
              backgroundColor: '#2B7FFF',
            },
          },
        }}
      />
    </div>
  );
}

function CardFooter({
  workspace,
  hasResumePermission,
  onResume,
}: {
  workspace: WorkspaceT;
  hasResumePermission: boolean;
  onResume: () => void;
}) {
  const [css, theme] = useStyletron();
  const navigate = useNavigate();
  const [, setOnboardingState] = useAtom(onboardingAtom);

  const enabled = isStatusActive(workspace.status) && workspace.canAccess;
  const showResumeButton = isStatusPaused(workspace.status) || isStatusIdle(workspace.status);

  const firstRowButtonOverrides = {
    BaseButton: {
      style: {
        flex: 1,
        fontSize: '12px',
        ...expand({
          padding: '6px',
        }),
      },
    },
  };

  const secondRowButtonOverrides = {
    BaseButton: {
      style: {
        flex: 1,
        fontSize: '18px',
        height: '44px',
        ...expand({
          padding: '12px 16px',
        }),
      },
    },
  };

  if (showResumeButton) {
    return (
      <div
        className={css({
          display: 'flex',
          gap: '8px',
          height: '92px',
        })}
      >
        <Button kind="primary" onClick={onResume} disabled={!hasResumePermission} overrides={secondRowButtonOverrides}>
          Resume
        </Button>
      </div>
    );
  }

  return (
    <div
      className={css({
        display: 'flex',
        flexDirection: 'column',
        gap: '16px',
      })}
    >
      <div
        className={css({
          display: 'flex',
          gap: '32px',
        })}
      >
        <Button
          kind="secondary"
          onClick={() => navigate('/design-schema?workspace_id=' + workspace.workspace_id)}
          disabled={!enabled}
          overrides={firstRowButtonOverrides}
        >
          Design Schema
        </Button>
        <Button
          kind="secondary"
          onClick={() => navigate('/ingestion?workspace_id=' + workspace.workspace_id)}
          disabled={!enabled}
          overrides={firstRowButtonOverrides}
        >
          Load Data
        </Button>
        <Button
          kind="secondary"
          onClick={() => navigate(`/explore?workspace_id=${encodeURIComponent(workspace.workspace_id)}`)}
          disabled={!enabled}
          overrides={firstRowButtonOverrides}
        >
          Explore Graph
        </Button>
      </div>
      <div
        className={css({
          display: 'flex',
          gap: '8px',
        })}
      >
        <Button
          kind="primary"
          onClick={() => {
            setOnboardingState({
              ...defaultOnboardingState,
              workspace: workspace,
            });
            navigate(`/onboarding/import-csv?workspace_id=${workspace.workspace_id}&source=card_view`);
          }}
          disabled={!enabled}
          overrides={secondRowButtonOverrides}
        >
          Build AI
        </Button>
        <Button
          kind="secondary"
          onClick={() => {
            trackUtil.trackEnterGSQLEditor('card view');
            navigate(`/editor?workspace_id=${encodeURIComponent(workspace.workspace_id)}`);
          }}
          disabled={!enabled}
          overrides={secondRowButtonOverrides}
        >
          GSQL Editor
        </Button>
      </div>
    </div>
  );
}

function AccessBadge({ isRw }: { isRw: boolean }) {
  const [css] = useStyletron();
  return (
    <span
      className={css({
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        minWidth: '40px',
        padding: '2px 8px',
        borderRadius: '4px',
        border: `1px solid ${isRw ? '#BEDBFF' : '#E5E7EB'}`,
        color: isRw ? '#1447E6' : '#364153',
        fontSize: '12px',
        fontWeight: 500,
        backgroundColor: isRw ? '#EFF6FF' : '#F9FAFB',
      })}
    >
      {isRw ? 'R/W' : 'R/O'}
    </span>
  );
}

function SnapshotSection({
  group,
  databaseName,
  snapshots,
  databaseById,
  selectedIds,
  onToggleRow,
}: {
  group: WorkGroupT;
  databaseName: string;
  snapshots: WorkspaceT[];
  databaseById: Record<string, DatabaseT>;
  selectedIds: Set<string>;
  onToggleRow: (workspace: WorkspaceT) => void;
}) {
  const [css, theme] = useStyletron();
  const [expanded, setExpanded] = useState(false);
  const hasSnapshots = snapshots.length > 0;

  return (
    <div
      className={css({
        borderTop: `1px solid ${theme.colors.divider}`,
        paddingTop: '12px',
      })}
    >
      {hasSnapshots ? (
        <>
          <button
            type="button"
            onClick={() => setExpanded(!expanded)}
            className={css({
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              width: '100%',
              padding: 0,
              border: 'none',
              backgroundColor: 'transparent',
              cursor: 'pointer',
              color: theme.colors['text.primary'],
            })}
          >
            <div className={css({ display: 'flex', alignItems: 'center', gap: '8px' })}>
              <span className={css({ fontSize: '13px', fontWeight: 500 })}>{databaseName}</span>
              <span
                className={css({
                  fontSize: '11px',
                  fontWeight: 600,
                  color: '#D97706',
                  backgroundColor: '#FEF3C7',
                  borderRadius: '4px',
                  padding: '2px 8px',
                  lineHeight: '16px',
                })}
              >
                Snapshot
              </span>
            </div>
            {expanded ? (
              <ChevronUp size={16} color={theme.colors['text.secondary']} />
            ) : (
              <ChevronDown size={16} color={theme.colors['text.secondary']} />
            )}
          </button>

          {expanded ? (
            <div className={css({ display: 'flex', flexDirection: 'column', gap: '16px', marginTop: '12px' })}>
              {snapshots.map((snapshot) => {
                const db = snapshot.database_id ? databaseById[snapshot.database_id] : undefined;
                return (
                  <SnapshotWorkspaceDetails
                    key={snapshot.workspace_id}
                    group={group}
                    workspace={snapshot}
                    database={db}
                    checked={selectedIds.has(snapshot.workspace_id)}
                    onToggle={() => onToggleRow(snapshot)}
                  />
                );
              })}
            </div>
          ) : null}
        </>
      ) : (
        <span className={css({ fontSize: '13px', fontWeight: 500, color: theme.colors['text.primary'] })}>
          No Snapshots
        </span>
      )}
    </div>
  );
}

function SnapshotWorkspaceDetails({
  group,
  workspace,
  database,
  checked,
  onToggle,
}: {
  group: WorkGroupT;
  workspace: WorkspaceT;
  database: DatabaseT | undefined;
  checked?: boolean;
  onToggle?: () => void;
}) {
  return (
    <WorkspaceCardBody
      group={group}
      workspace={workspace}
      database={database}
      showDatabaseRow={false}
      checked={checked}
      onToggle={onToggle}
    />
  );
}
